// Single source of editable church content. Items with `confirm: true` await leadership confirmation.
export const SHOW_CONFIRM_TAGS = true;

export const SITE = {
  name: "GNP Church",
  longName: "God's Name Proclaimed",
  canonicalBase: "https://gnpchurch.com", // proposed domain, change once ownership is confirmed
  motto: ["FEAR GOD.", "LOVE GOD.", "OBEY GOD."],
  youtube: "https://www.youtube.com/@GNPCHURCH7",
  instagram: null, // add the official Instagram URL when available
  email: "gnpchurch3@gmail.com",
  phone: null,
  address: { street: "4570 Lockwood Blvd", city: "Youngstown", region: "OH", zip: "44511" },
  sunday: { day: "Sunday", time: "2:30 PM" },
  mission:
    "To glorify Jesus Christ, prepare believers to stand firm through the coming trials, and call the lost home.",
};

export const ADDRESS_LINE = `${SITE.address.street}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.zip}`;
export const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ADDRESS_LINE)}`;
export const MAP_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS_LINE)}&output=embed`;

export const IMAGES = {
  logo: "/images/gnp-logo.webp",
  hero: "/images/hero-sanctuary.webp",
  heroSm: "/images/hero-sanctuary-sm.webp",
  heroMobile: "/images/hero-sanctuary-mobile.webp",
  prayer: "/images/prayer-altar.webp",
  isaiah: "/images/isaiah-harvey.webp",
  cambrelle: "/images/cambrelle-patterson.webp",
  dom: "/images/dom-patterson.webp",
  bibleStudy: "/images/bible-study.webp",
  worshipArms: "/images/worship-open-arms.webp",
  layingHands: "/images/prayer-laying-hands.webp",
  fellowship: "/images/worship-fellowship.webp",
  street: "/images/street-evangelism.webp",
};

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/identity", label: "Our Identity" },
  { to: "/leadership", label: "Leadership" },
  { to: "/ministries", label: "Ministries" },
  { to: "/sermons", label: "Sermons" },
  { to: "/visit", label: "Visit Us" },
];

export const PILLARS = [
  {
    word: "FEAR",
    text: "Deep reverence for God that shapes how we live, worship, and walk in His truth.",
    verse: "The fear of the LORD is the beginning of wisdom.",
    ref: "Proverbs 9:10 (KJV)",
  },
  {
    word: "LOVE",
    text: "Wholehearted devotion to God expressed through love, worship, faithfulness, and daily obedience.",
    verse: "Thou shalt love the Lord thy God with all thy heart.",
    ref: "Matthew 22:37 (KJV)",
  },
  {
    word: "OBEY",
    text: "Putting the Word into practice and following Christ beyond words alone.",
    verse: "Be ye doers of the word, and not hearers only.",
    ref: "James 1:22 (KJV)",
  },
];

export const IDENTITY_THEMES = [
  { title: "Jesus Christ at the center", text: "Everything we do points to Jesus: His life, His death, His resurrection, and His lordship over every part of life." },
  { title: "Scripture-centered teaching", text: "We open the Bible and teach what it says, often verse by verse, so believers can understand it and live it." },
  { title: "Prayer & corporate worship", text: "We seek God together. Prayer and worship are not add-ons to our gatherings; they are the heart of them." },
  { title: "Discipleship & growth", text: "Following Jesus is a lifelong walk. We help one another grow in maturity, consistency, and accountability." },
  { title: "Evangelism & reaching the lost", text: "We take the gospel beyond the four walls, into the streets and neighborhoods of Youngstown." },
  { title: "Genuine fellowship", text: "We are a family, not an audience. We carry each other's burdens and build real relationships in Christ." },
  { title: "Faithful through trials", text: "We prepare believers to stand firm when life gets hard, rooted in truth rather than circumstance." },
  { title: "Youngstown & beyond", text: "Our city is our first mission field. Our calling is to proclaim the name of Jesus wherever God sends us." },
];

export const COMMITMENTS = [
  { term: "Non-denominational", plain: "We are a Christian assembly that is not part of a denomination. Our allegiance is to Jesus Christ and His Word." },
  { term: "Word-centered", plain: "The Bible is the authority for what we teach and how we live. Preaching flows from the text, not from opinion." },
  { term: "Apostolic emphasis", plain: "We hold to the foundation laid by the apostles in the New Testament: sound teaching, prayer, sending, and building up the church.", confirm: true },
  { term: "Evangelistic mission", plain: "We exist to call the lost home. Sharing the gospel is part of our ordinary Christian life." },
  { term: "Prayer, worship & fellowship", plain: "Like the early church in Acts 2:42, we devote ourselves to teaching, fellowship, the breaking of bread, and prayer." },
];

export const MILESTONES = [
  {
    date: "September 20, 2026",
    title: "Corporate Recognition & Commissioning",
    text: "GNP Church's corporate recognition and commissioning ceremony.",
    confirm: true,
  },
];

export const LEADERS = [
  {
    id: "dom-patterson",
    name: "Minister Dom Patterson",
    aka: "DJ Patterson",
    title: "Founder",
    image: IMAGES.dom,
    alt: "Minister Dom Patterson kneeling on the pavement, pointing to an open Bible",
    initials: "DP",
    bio: "Minister Dom Patterson is the founder of GNP Church alongside Cambrelle Patterson and co-leader Isaiah Harvey. Minister Dom serves in ministry at GNP Church with an emphasis on biblical teaching, discipleship, and helping believers remain steadfast through life's trials. His teaching explores the practical demands of following Christ, processing pain through a biblical lens, and developing an unshaken faith.",
    themes: ["The Book of James", "Perseverance", "Spiritual Maturity", "The Cost of Discipleship"],
  },
  {
    id: "isaiah-harvey",
    name: "Minister Isaiah Harvey",
    title: "Co-Leader",
    image: IMAGES.isaiah,
    alt: "Minister Isaiah Harvey speaking into a red microphone inside the GNP sanctuary",
    bio: "Minister Isaiah Harvey emphasizes accountability, the real cost of following Christ, and the importance of living faithfully in response to God's Word. His ministry encourages believers to grow in spiritual maturity and faithfully pursue their God-given responsibilities.",
    themes: ["Accountability", "The Cost of Following Christ", "Faithful Living"],
  },
  {
    id: "cambrelle-patterson",
    name: "Minister Cambrelle Patterson",
    title: "GNP Leadership",
    image: IMAGES.cambrelle,
    alt: "Minister Cambrelle Patterson kneeling in prayer in warm afternoon light",
    bio: "Cambrelle Patterson serves with a heart for Christ, worship, and community. She reflects a desire to encourage believers, cultivate genuine fellowship, and help build a church family rooted in love for God and one another.",
    themes: ["Worship", "Fellowship", "Community"],
  },
];

export const COVERING = {
  names: "Justin & Lauren Burnette",
  title: "Spiritual Covering",
  text: "Justin and Lauren Burnette provide spiritual covering for GNP Church.",
  confirm: true,
};

export const GATHERINGS = [
  {
    id: "sunday",
    day: "Sunday",
    time: "2:30 PM",
    name: "The Sunday Gathering",
    where: ADDRESS_LINE,
    lead: "Passionate, Scripture-centered, and focused on following Jesus.",
    focus: ["Worship", "Prayer", "Verse-by-verse biblical teaching", "Fellowship", "Christian growth & discipleship"],
  },
  {
    id: "wednesday",
    day: "Wednesday",
    time: "7:00 PM",
    name: "Midweek Discipleship",
    where: "Women & Men Bible Studies on Instagram",
    lead: "Detailed study that builds a community of men and women growing together in Christ.",
    focus: ["Detailed biblical study", "The structure and message of Scripture", "Studying books of the Bible", "Spiritual growth & accountability"],
    note: "Contact GNP for current details and how to join.",
    image: IMAGES.bibleStudy,
    alt: "GNP members praying together around tables with open Bibles during Bible study",
  },
  {
    id: "saturday",
    day: "Saturday",
    time: "Time TBA",
    name: "Street Evangelism & Outreach",
    where: "Youngstown & the Mahoning Valley",
    lead: "Taking the name of Jesus beyond the church walls.",
    focus: ["Sharing the gospel", "Proclaiming the name of Jesus", "Reaching people beyond church walls", "Practical care for neighbors"],
    note: "Contact GNP for current details.",
    image: IMAGES.street,
    alt: "A GNP member lays a hand on a man's shoulder and prays with him on a city sidewalk during street outreach",
  },
];

export const SERMON_CATEGORIES = [
  {
    id: "alignment-cost",
    name: "Alignment & Cost",
    themes: ["The gospel and personal surrender", "John 3 and spiritual transformation", "Crucifying personal ego", "The cost of following Christ"],
  },
  {
    id: "unshaken-faith",
    name: "Unshaken Faith",
    themes: ["The book of James", "Perseverance through trials", "Grief, suffering, and faithfulness", "Spiritual maturity and consistency"],
  },
  {
    id: "the-epistles",
    name: "The Epistles",
    themes: ["Systematic biblical exposition", "Understanding the New Testament letters", "Doctrine and Christian living", "Discipleship rooted in Scripture"],
  },
];

export const EXPECT = [
  { title: "Worship", text: "Heartfelt, Christ-focused worship. Come as you are; no one expects you to know the songs." },
  { title: "Biblical teaching", text: "A message that opens the Scriptures and applies them directly to everyday life." },
  { title: "Prayer", text: "Time to seek God together. If you'd like prayer, simply ask; we'd be honored." },
  { title: "Community", text: "Real people who want to know you. Stay after, ask questions, and meet the family." },
];
